// components/Button.tsx
import React, { ButtonHTMLAttributes, useContext } from "react";
import { Loader2 } from "lucide-react";
import { twMerge } from "tailwind-merge";
import { CMSModal } from "@/context";

export type Variant = "primary" | "outline" | "ghost";

type ButtonProps = {
  disabled?: boolean;
  loading?: boolean;
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  variant?: Variant;
} & ButtonHTMLAttributes<HTMLButtonElement>;

const Button: React.FC<ButtonProps> = ({
  type = "button",
  disabled = false,
  loading = false,
  children,
  onClick,
  className = "",
  variant = "primary",
  ...props
}) => {
  const { theme } = useContext(CMSModal);
  const baseClasses = "w-full h-10 rounded transition cursor-pointer flex items-center justify-center gap-2";

  const variantClasses: Record<Variant, string> = {
    primary: "bg-[#099ed1] text-white hover:bg-[#25a9d7]",
    outline: twMerge(
      "border border-[#099ed1] text-[#099ed1] transition-colors",
      theme === "dark" ? "bg-transparent hover:bg-[#1a3a4a]" : "bg-white hover:bg-[#e6f0f8]"
    ),
    ghost: twMerge(
      "bg-transparent transition-colors",
      theme === "dark" ? "text-gray-300 hover:bg-gray-800" : "text-[#004175] hover:bg-gray-100"
    ),
  };

  const disabledClasses = "opacity-50 cursor-not-allowed";

  const mergedClassName = twMerge(
    baseClasses,
    variantClasses[variant],
    (disabled || loading) && disabledClasses,
    className
  );

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={mergedClassName}
    >
      {loading ? (
        <>
          <Loader2 size={15} className="animate-spin" />
          {children}
        </>
      ) : (
        children
      )}
    </button>
  );
};

export default Button;
